<script setup lang="ts">
/* Types */
import { Modification, Product } from '@/types/api/api-v3/product'
import {
  ProductComponent,
  ProductType,
} from '@/types/components/product-card.types'

/* Components */
import AppMeasureUnits from '@/components/App/AppMeasureUnits.vue'

type Props = {
  product: Product
  productType: ProductType
  currentModification: Modification
  component: ProductComponent
}

const props = defineProps<Props>()

const emit = defineEmits(['on-open-dialog'])

const { isDesktop } = useDevice()

const productTitleElement = ref<HTMLElement | null>(null)

const productTitleElementHeight = computed(() => {
  return productTitleElement?.value?.getBoundingClientRect().height
})

const productTitle = computed(() => {
  if (props.product.modifications.length >= 2) {
    return `${props.product.name}`
  }

  return props.product.modifications[0].mode
    ? `${props.product.name}: ${props.product.modifications[0].mode}`
    : `${props.product.name}`
})

const isExtra = ref(false) // TODO: Удалить после того как бэкенд будет отдавать поле
</script>

<template>
  <component
    :is="component.tag"
    v-bind="component.attrs"
    class="product-card-content"
    @click.prevent="emit('on-open-dialog')"
  >
    <div class="product-card-content__header">
      <span ref="productTitleElement" class="product-card-content__title">
        {{ productTitle }}
      </span>

      <nuxt-img
        v-if="isExtra"
        src="/product/hot.svg"
        width="12"
        height="12"
        alt="Острое"
        class="product-card-content__extra"
      />

      <app-measure-units
        v-if="productType === 'simple'"
        class="product-card-content__weight"
        :weight="currentModification.weight"
        :volume-in-liters="currentModification.volume_in_liters"
        :measure="currentModification.measure"
      />
    </div>

    <div
      v-if="isDesktop"
      class="product-card-content__text"
      :class="{
        'product-card-content__text--short': productTitleElementHeight > 20,
      }"
    >
      {{ currentModification.description }}
    </div>
  </component>
</template>

<style lang="scss" scoped>
.product-card-content {
  display: block;
  padding: 0 8px;

  @include media('>=desktop-mini') {
    padding: 0 16px;
    line-height: 21px;
  }

  &__header {
    max-height: 36px;
    overflow: hidden;
    font-weight: 600;
    line-height: 18px;

    @include media('>=desktop-mini') {
      max-height: 42px;
      line-height: 21px;
    }
  }

  &__title {
    margin-right: 4px;
    font-size: 14px;
    color: var(--ui-text-primary);
    word-break: break-word;

    @include media('>=desktop-mini') {
      font-size: 16px;
    }
  }

  &__extra {
    margin-right: 4px;
  }

  &__weight {
    font-size: 12px;
    color: var(--ui-text-secondary);

    @include media('>=desktop-mini') {
      font-size: 14px;
    }
  }

  &__text {
    display: none;

    @include media('>=desktop-mini') {
      display: -webkit-box;
      margin-top: 4px;
      overflow: hidden;
      font-size: 14px;
      font-weight: 600;
      line-height: 18px;
      color: var(--ui-text-secondary);
      text-overflow: ellipsis;
      -webkit-line-clamp: 3;
      line-clamp: 3;
      -webkit-box-orient: vertical;

      &--short {
        -webkit-line-clamp: 2;
        line-clamp: 2;
      }
    }
  }
}
</style>
